import { TranslateService } from '@ngx-translate/core';

/** Erreurs de formulaire renvoyées par l'API : `form_errors` → champ → messages. */
export type FormErrors = Record<string, string[]>;

/** Item d'erreur structuré (FormErrorsException normalisée côté API). */
interface FormErrorItem {
  message: string;
}

/** Erreurs d'un lot de records réparties par position de record. */
export interface FormErrorsByRecord {
  /** Erreurs rattachées à un record du lot (index → champ nu → messages). */
  byIndex: Map<number, FormErrors>;
  /** Erreurs non rattachables à un record (erreur globale, index hors lot). */
  leftovers: FormErrors;
}

/** Namespace i18n front des messages serveur (`API_ERRORS.<domaine>.<clé>`). */
export const API_ERRORS_I18N_NAMESPACE = 'API_ERRORS';

/** Clé d'erreur d'un lot : `datas[<index>].<champ>` (cf. api-data createManyRecords). */
const BATCH_ERROR_KEY_PATTERN = /^datas\[(\d+)\]\.(.+)$/;

export class FormErrorsUtils {
  /**
   * Traduit les messages serveur (clés `domaine.cle`, ex.
   * `validation.field_validation.too_long`) via le namespace `API_ERRORS`.
   * Un message sans traduction est conservé tel quel (texte déjà lisible ou
   * clé inconnue).
   */
  public static translate(
    errors: Record<string, unknown>,
    translateService: TranslateService
  ): FormErrors {
    const translated: FormErrors = {};
    Object.entries(errors).forEach(([field, value]) => {
      translated[field] = FormErrorsUtils.toMessages(value).map((message) =>
        FormErrorsUtils.translateMessage(message, translateService)
      );
    });
    return translated;
  }

  /**
   * Répartit les erreurs d'une création groupée entre les `recordCount`
   * records du lot. L'API préfixe les clés par la position du record fautif
   * (`datas[<i>].<champ>`) quand le lot en compte plusieurs ; avec un seul
   * record, les clés sont nues et visent ce record. Tout le reste (erreur
   * globale, index hors lot) est renvoyé dans `leftovers`.
   */
  public static groupByRecord(
    errors: FormErrors,
    recordCount: number
  ): FormErrorsByRecord {
    const byIndex = new Map<number, FormErrors>();
    const leftovers: FormErrors = {};
    const assign = (index: number, field: string, messages: string[]) => {
      const recordErrors = byIndex.get(index) ?? {};
      recordErrors[field] = messages;
      byIndex.set(index, recordErrors);
    };
    Object.entries(errors).forEach(([key, messages]) => {
      const match = key.match(BATCH_ERROR_KEY_PATTERN);
      if (match && Number(match[1]) < recordCount) {
        assign(Number(match[1]), match[2], messages);
      } else if (!match && recordCount === 1) {
        assign(0, key, messages);
      } else {
        leftovers[key] = messages;
      }
    });
    return { byIndex, leftovers };
  }

  public static isEmpty(errors: FormErrors): boolean {
    return Object.keys(errors).length === 0;
  }

  /**
   * Normalise la valeur d'un champ de `form_errors` en liste de messages :
   * `string[]` (cas nominal), `string` (feuille scalaire), ou items structurés
   * `{ message }`.
   */
  private static toMessages(value: unknown): string[] {
    const items = Array.isArray(value) ? value : [value];
    return items.map((item) => {
      if (typeof item === 'string') return item;
      if (FormErrorsUtils.isErrorItem(item)) return item.message;
      return String(item);
    });
  }

  private static isErrorItem(item: unknown): item is FormErrorItem {
    return (
      typeof item === 'object' &&
      item !== null &&
      typeof (item as FormErrorItem).message === 'string'
    );
  }

  /**
   * Traduit un message serveur isolé (clé `domaine.cle`) via le namespace
   * `API_ERRORS`. Sans traduction, le message est renvoyé tel quel.
   */
  public static translateMessage(
    message: string,
    translateService: TranslateService
  ): string {
    const key = `${API_ERRORS_I18N_NAMESPACE}.${message}`;
    const translation = translateService.instant(key);
    return translation === key ? message : translation;
  }
}
